import { TemplatePreviewLayout } from "@/components/template/template-preview-layout";
import {
  TemplateLanguageDetails,
  TemplateSchemaDetails,
} from "@/components/template/template-preview-details";
import { parsePreviewSchema } from "@/components/template/template-preview-data";
import { TemplateErrorComponent } from "@/components/error";
import { AnalyticsEvents, track } from "@/lib/analytics";
import { toError } from "@/lib/to-error";
import { PendingTemplateComponent } from "@/components/pending";
import { DraftTemplatePreview } from "@/components/template/draft-template-preview";
import { TemplateContentPreview } from "@/components/template/template-content-preview";
import type { ProjectBySlugOutput } from "@/types";
import { SyncTemplate } from "@/components/template/sync-template";
import { TemplateDuplicateDialog } from "@/components/template/template-duplicate-dialog";
import { orpc } from "@/lib/orpc";
import { projects, type LocaleEnum } from "@papier/projects-schema";
import { templates } from "@papier/templates-schema";
import { zodSlug } from "@papier/shared";
import { Badge } from "@papier/ui/components/ui/badge";
import { Button } from "@papier/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@papier/ui/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@papier/ui/components/ui/dropdown-menu";
import { cn } from "@papier/ui/lib/utils";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { formatDate, formatDistanceToNow } from "date-fns";
import {
  TransferHorizontal as ArrowLeftRight,
  Copy,
  Pen as Edit,
  History,
  Widget2 as LayoutDashboard,
  Letter as Mail,
  MenuDots as MoreHorizontal,
  Server,
  ShieldCheck,
} from "@papier/ui/components/icons";
import { useRef, useState } from "react";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@papier/ui/components/ui/alert-dialog";
import { Refresh as Loader2 } from "@papier/ui/components/icons";
import { toast } from "sonner";
import { useSession } from "@/lib/auth-client";
import { SendTestEmailWithVariablesDialog } from "@papier/template-editor";
import type { VariablesRoot } from "@papier/render-engine";

const templateSearchSchema = z.object({
  env: zodSlug().optional(),
  version: z
    .string()
    .regex(/^\d+\.\d+\.\d+$/)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute("/_app/projects/$projectSlug/templates/$templateSlug/")({
  component: TemplateIndex,
  wrapInSuspense: true,
  params: {
    parse: (params) => {
      return z
        .object({
          projectSlug: projects.shape.slug,
          templateSlug: templates.shape.slug,
        })
        .parse(params);
    },
  },
  validateSearch: templateSearchSchema,
  pendingComponent: () => <PendingTemplateComponent />,
  errorComponent: ({ error }) => <TemplateErrorComponent error={toError(error)} />,
  loaderDeps: ({ search: { env, version } }) => ({ env, version }),
  loader: async ({
    context: { queryClient, orpc },
    params: { projectSlug, templateSlug },
    deps: { env, version },
  }) => {
    const projectData = await queryClient.ensureQueryData(
      orpc.projects.getBySlug.queryOptions({ input: { projectSlug } }),
    );
    if (!projectData) {
      notFound();
    }

    const draft = await queryClient.fetchQuery({
      ...orpc.templates.getDraft.queryOptions({
        input: { projectId: projectData.id, templateSlug },
      }),
      staleTime: 0,
    });
    if (draft.status === "draft") {
      return { kind: "draft" as const, projectData, templateData: draft, environmentSlug: env };
    }
    const environmentSlug = env || projectData.environments[0]?.slug;
    if (!environmentSlug) {
      throw new Error("No environments found for this project");
    }
    if (!env) {
      throw redirect({
        to: "/projects/$projectSlug/templates/$templateSlug",
        params: { projectSlug, templateSlug },
        search: { env: environmentSlug, version },
        replace: true,
      });
    }
    const templateData = await queryClient.ensureQueryData(
      orpc.templates.getPublishedTemplate.queryOptions({
        input: {
          projectId: projectData.id,
          templateSlug,
          environmentSlug,
          version,
        },
      }),
    );

    return { kind: "published" as const, projectData, templateData, environmentSlug };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: `Papier - ${loaderData?.templateData.name || "Template"}` }],
  }),
});

function TemplateIndex() {
  const data = Route.useLoaderData();
  const { templateSlug } = Route.useParams();
  if (data.kind === "draft") {
    return (
      <DraftTemplatePreview
        key={data.templateData.id}
        project={data.projectData}
        template={data.templateData}
        templateSlug={templateSlug}
        environmentSlug={data.environmentSlug}
      />
    );
  }
  return (
    <PublishedTemplatePreview
      key={`${data.templateData.id}-${data.environmentSlug}`}
      projectData={data.projectData}
      environmentSlug={data.environmentSlug}
    />
  );
}

function PublishedTemplatePreview({
  projectData,
  environmentSlug,
}: {
  projectData: ProjectBySlugOutput;
  environmentSlug: string;
}) {
  const { useParams, useSearch, useNavigate, Link } = Route;
  const { templateSlug } = useParams();
  const { version } = useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const { data: approvalPolicy } = useSuspenseQuery(
    orpc.templates.getReviewPolicy.queryOptions({ input: { projectId: projectData.id } }),
  );

  const { data: template } = useSuspenseQuery(
    orpc.templates.getPublishedTemplate.queryOptions({
      input: {
        templateSlug,
        projectId: projectData.id,
        environmentSlug: environmentSlug,
        version: version ?? undefined,
      },
    }),
  );

  const [restoringVersion, setRestoringVersion] = useState<string | null>(null);

  const { mutateAsync: restoreVersionMutate, isPending: isRestorePending } = useMutation(
    orpc.templates.restore.mutationOptions({
      onSuccess: () => {
        track(AnalyticsEvents.TemplateRestored);
        toast.success("Template restored successfully");
        return queryClient.invalidateQueries({ queryKey: orpc.templates.key() });
      },
      onError: (error) => {
        toast.error("Failed to restore template", {
          description: error.message,
        });
      },
    }),
  );

  const handleRestore = async (targetVersion: string) => {
    try {
      setRestoringVersion(targetVersion);
      await restoreVersionMutate({
        templateId: template.id,
        version: targetVersion,
        environmentIds: template.environments.map((envItem) => envItem.id),
      });
      void navigate({ search: (prev) => ({ ...prev, version: undefined }) });
    } finally {
      setRestoringVersion(null);
    }
  };

  const currentEnvironment = template.environments.find(
    (envItem) => envItem.slug === environmentSlug,
  );

  const [currentLanguage, setCurrentLanguage] = useState<LocaleEnum>(
    template.locales[0] || "en-US",
  );
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [isSendTestEmailDialogOpen, setIsSendTestEmailDialogOpen] = useState(false);
  const testEmailTrigger = useRef<HTMLButtonElement>(null);

  const { mutateAsync: sendTestEmailMutate, isPending: isSendingTestEmail } = useMutation(
    orpc.previewSend.sendTestEmail.mutationOptions({
      onSuccess: () => {
        track(AnalyticsEvents.TestSend, { channel: "email" });
        toast.success("Test email sent successfully", {
          description:
            "Previews leave from Papier's shared pool, so they may land in spam. Check the recipient's spam folder.",
        });
        setIsSendTestEmailDialogOpen(false);
      },
      onError: (error) => {
        toast.error("Failed to send test email", {
          description: error.message,
        });
      },
    }),
  );

  const handleSendTestEmail = async (variables: VariablesRoot) => {
    const localeContent = template.localeContent.find((lc) => lc.locale === currentLanguage);
    if (!localeContent) return;

    await sendTestEmailMutate({
      templateContentId: localeContent.id,
      variables,
      emailPreheader: localeContent.content?.emailPreheader ?? "",
    });
  };

  const schema = parsePreviewSchema(template.schemaDefinition);
  const content = template.localeContent.find((lc) => lc.locale === currentLanguage)?.content;
  if (!template || !content || !currentEnvironment) {
    return null;
  }

  return (
    <>
      <TemplatePreviewLayout
        key={template.version}
        project={projectData}
        template={template}
        environmentSlug={environmentSlug}
        status="published"
        metadata={
          <>
            <span className="inline-flex items-center gap-1.5 border-l border-border pl-3">
              <Server className="size-4" aria-hidden="true" />
              {currentEnvironment.name}
            </span>
            <span className="border-l border-border pl-3">v{template.version}</span>
          </>
        }
        actions={
          <>
            {approvalPolicy.mode !== "open" && (
              <Button variant="outline" asChild>
                <Link
                  to="/projects/$projectSlug/templates/$templateSlug/review"
                  search={{ env: environmentSlug }}
                >
                  <ShieldCheck />
                  Review draft
                </Link>
              </Button>
            )}
            {template.type === "email" && (
              <Button
                ref={testEmailTrigger}
                variant="outline"
                aria-label="Send test email"
                onClick={() => setIsSendTestEmailDialogOpen(true)}
              >
                <Mail />
                <span className="hidden sm:inline">Send test email</span>
                <span className="sm:hidden">Send test</span>
              </Button>
            )}
            <Button asChild>
              <Link
                aria-label="Edit template"
                to="/projects/$projectSlug/templates/$templateSlug/edit"
                search={{ env: environmentSlug }}
              >
                <Edit />
                Edit template
              </Link>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" aria-label="More template actions">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setIsDuplicateDialogOpen(true)}>
                  <Copy />
                  Duplicate
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
        notice={
          <>
            {template.version !== currentEnvironment.version && (
              <div
                role="status"
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 p-4 text-sm"
              >
                <p>
                  Viewing v{template.version}. {currentEnvironment.name} is using v
                  {currentEnvironment.version}.
                </p>
                <Button
                  variant="outline"
                  onClick={() => navigate({ search: (prev) => ({ ...prev, version: undefined }) })}
                >
                  View current version
                </Button>
              </div>
            )}
            {(template.areEnvsOutOfSync || !template.isEveryEnvironmentLatest) && (
              <SyncTemplate
                templateId={template.id}
                environments={template.environments}
                versions={template.versions}
              />
            )}
          </>
        }
        preview={
          <TemplateContentPreview
            name={template.name}
            type={template.type}
            title={`${version ? "Version preview" : "Live preview"} · v${template.version}`}
            content={content}
            schema={schema}
            locales={template.locales}
            currentLanguage={currentLanguage}
            onLanguageChange={setCurrentLanguage}
          />
        }
        details={
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LayoutDashboard className="size-5" />
                  Template info
                </CardTitle>
                {template.description && <CardDescription>{template.description}</CardDescription>}
              </CardHeader>
              <CardContent>
                <dl className="space-y-3 text-sm">
                  <div className="flex flex-wrap justify-between gap-2">
                    <dt className="text-muted-foreground">Created</dt>
                    <dd>{formatDate(template.createdAt, "MMM d, yyyy")}</dd>
                  </div>
                  <div className="flex flex-wrap justify-between gap-2">
                    <dt className="text-muted-foreground">Updated</dt>
                    <dd title={formatDate(template.updatedAt, "MMM d, yyyy HH:mm")}>
                      {formatDistanceToNow(template.updatedAt, { addSuffix: true })}
                    </dd>
                  </div>
                  <div className="space-y-1 border-t border-border pt-3">
                    <dt className="text-muted-foreground">Created by</dt>
                    <dd className="font-medium break-words">
                      {template.createdBy?.name || "Unknown"}
                    </dd>
                    <dd className="break-words text-muted-foreground">
                      {template.createdBy?.email || "Unknown"}
                    </dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="size-5" />
                  Environments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {template.environments.map((envItem) => (
                    <li
                      key={envItem.id}
                      className="flex flex-wrap items-center justify-between gap-2 text-sm"
                    >
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="font-medium break-words">{envItem.name}</span>
                        <span className="text-muted-foreground">v{envItem.version}</span>
                      </div>
                      {environmentSlug === envItem.slug ? (
                        <Badge variant="secondary">Current</Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          aria-label={`Switch to ${envItem.name}`}
                          onClick={() =>
                            navigate({
                              search: (prev) => ({
                                ...prev,
                                env: envItem.slug,
                                version: undefined,
                              }),
                            })
                          }
                        >
                          <ArrowLeftRight />
                          View
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="size-5" />
                  Versions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {template.versions.map((item, index) => {
                  const isActive = item.version === template.version;
                  const isLatestVersion = index === 0;
                  const isVersionRestoring = restoringVersion === item.version;
                  const [currentMajor, currentMinor, currentPatch] = (
                    template.versions[0]?.version || "0.0.0"
                  )
                    .split(".")
                    .map(Number);
                  const nextVersion = `${currentMajor}.${currentMinor}.${(currentPatch || 0) + 1}`;
                  return (
                    <div
                      key={`${item.slug}-${item.version}`}
                      className="flex min-w-0 items-center gap-2"
                    >
                      <Button
                        asChild
                        variant="ghost"
                        className={cn(
                          "min-w-0 flex-1 justify-between",
                          isActive && "border-border bg-accent",
                        )}
                      >
                        <Link
                          to="."
                          search={{ env: environmentSlug, version: item.version }}
                          aria-current={isActive ? "true" : undefined}
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <span>v{item.version}</span>
                            {isLatestVersion && <Badge variant="outline">Latest</Badge>}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(item.createdAt, "MMM d, yyyy")}
                          </span>
                        </Link>
                      </Button>
                      {!isLatestVersion && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={`Restore version ${item.version}`}
                              disabled={isRestorePending}
                            >
                              {isVersionRestoring ? (
                                <Loader2 className="size-4 animate-spin motion-reduce:animate-none" />
                              ) : (
                                <History />
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Restore version v{item.version}</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will create a new version <strong>v{nextVersion}</strong> based
                                on the content and schema from v{item.version}. The new version will
                                be applied to all environments, replacing the current active
                                versions.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                disabled={isRestorePending}
                                onClick={() => void handleRestore(item.version)}
                              >
                                {isVersionRestoring ? "Restoring" : "Restore"}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
            <TemplateLanguageDetails locales={template.locales} />
            <TemplateSchemaDetails schema={schema} />
          </>
        }
      />
      <TemplateDuplicateDialog
        templateId={template.id}
        templateName={template.name}
        templateDescription={template.description}
        templateLocales={template.locales}
        projectId={projectData.id}
        open={isDuplicateDialogOpen}
        onOpenChange={setIsDuplicateDialogOpen}
      />
      {template.type === "email" && (
        <SendTestEmailWithVariablesDialog
          open={isSendTestEmailDialogOpen}
          onClose={() => setIsSendTestEmailDialogOpen(false)}
          onConfirm={handleSendTestEmail}
          userEmail={session?.user?.email || ""}
          isLoading={isSendingTestEmail}
          schema={schema}
          initialVariables={{}}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            testEmailTrigger.current?.focus();
          }}
        />
      )}
    </>
  );
}
